import type {ReactElement} from 'react';
import {useEffect} from 'react';
import React from 'react';
import {useDownloadPanelData} from '@Components/download-panel/download-panel.context';
import {
  CHEAP_PRODUCT_ID,
  EXPENSIVE_PRODUCT_ID,
  FREEBIE_PRODUCT_ID,
  getDisplayPriceForProduct,
  getProductIdForGifDownloadsForNonPremiumUsers,
  MAX_FREEBIE_DIMENSION,
  MAX_PAGES_ALLOWED_FOR_CHEAP_PRODUCT,
} from '@Libraries/poster-download-library';
import {Text, TextSize} from '@Components/text';
import type {BulletItem} from '@Components/download-panel/components/quality-item/quality-item';
import {QualityItem} from '@Components/download-panel/components/quality-item/quality-item';
import styles from '@Components/download-panel/components/download-video-quality-option/download-video-quality-option.module.scss';
import {TagType} from '@Components/tag';
import {getWidthAndHeightInPixels} from '@Libraries/poster-object.library';
import {SizeDropdownOptions} from '@Components/download-panel/components/download-design-size-option-wrapper/download-design-size-option-wrapper.types';

export function DownloadGifQualityOption(): ReactElement {
  const {
    productsData,
    appleDownloadProducts,
    selectedPages,
    isWebDownload,
    duration,
    setDuration,
    scale,
    setSelectedDisplaySizeOption,
    posterObject,
    setScale,
    selectedProductId,
    needsPurchase,
    setSelectedProductId,
  } = useDownloadPanelData();
  const CHEAP_PRODUCT = productsData?.find((productsData) => productsData.id === String(CHEAP_PRODUCT_ID));
  const EXPENSIVE_PRODUCT = productsData?.find((productsData) => productsData.id === String(EXPENSIVE_PRODUCT_ID));

  useEffect(() => {
    const productId = getProductIdForGifDownloadsForNonPremiumUsers(duration, scale, posterObject, isWebDownload, selectedPages.length);
    if (selectedProductId !== productId) {
      setSelectedProductId(productId);
    }
  }, [scale, duration, isWebDownload, selectedPages.length]);

  const getCheapProductTagText = (): string | undefined => {
    if (needsPurchase) {
      if (selectedProductId === EXPENSIVE_PRODUCT_ID) {
        return getDisplayPriceForProduct(EXPENSIVE_PRODUCT, appleDownloadProducts) ?? undefined;
      }
      return getDisplayPriceForProduct(CHEAP_PRODUCT, appleDownloadProducts) ?? undefined;
    }
    return undefined;
  };

  const getBulletsForFreeProduct = (): BulletItem[] => {
    const items = [
      {
        text: window.i18next.t('pmwjs_10_seconds_limit'),
        showSuccessBullet: false,
      },
    ];

    if (selectedPages.length > 1) {
      items.push({
        text: window.i18next.t('pmwjs_x_page_limit', {limit: MAX_PAGES_ALLOWED_FOR_CHEAP_PRODUCT + 1}),
        showSuccessBullet: false,
      });
    }
    return items;
  };

  return (
    <div className={`flex flex-v-row gap-2`}>
      <Text bold size={TextSize.XSMALL} val={window.i18next.t('pmwjs_quality')} />
      <div className={`flex-h-row gap-3`}>
        <QualityItem
          bulletsClassName={styles.bulletsContainer}
          id={`${FREEBIE_PRODUCT_ID}`}
          tagText={needsPurchase ? window.i18next.t('pmwjs_free') : undefined}
          onClick={(): void => {
            setSelectedProductId(FREEBIE_PRODUCT_ID);
            if (duration > 10) {
              setDuration(10);
            }

            const {width, height} = getWidthAndHeightInPixels(posterObject);
            if (Math.max(width, height) > MAX_FREEBIE_DIMENSION) {
              const newScale = MAX_FREEBIE_DIMENSION / Math.max(width, height);
              setScale(newScale);
              setSelectedDisplaySizeOption(SizeDropdownOptions.ORIGINAL_COMPRESSED);
            } else {
              setScale(1);
              setSelectedDisplaySizeOption(SizeDropdownOptions.SMALL);
            }
          }}
          text={window.i18next.t('pmwjs_basic_poster_download')}
          isSelected={selectedProductId === FREEBIE_PRODUCT_ID}
          bullets={getBulletsForFreeProduct()}
        />
        <QualityItem
          id={`gif-paid-product`}
          tagClassName={styles.secondaryTag}
          tagType={TagType.SECONDARY_LIGHT}
          tagText={getCheapProductTagText()}
          text={window.i18next.t('pmwjs_high')}
          onClick={(): void => {
            if (selectedPages.length > MAX_PAGES_ALLOWED_FOR_CHEAP_PRODUCT) {
              setSelectedProductId(EXPENSIVE_PRODUCT_ID);
              return;
            }
            setSelectedProductId(CHEAP_PRODUCT_ID);
          }}
          bullets={[
            {
              text: window.i18next.t('pmwjs_no_watermark'),
              showSuccessBullet: true,
            },
            {
              text: window.i18next.t('pmwjs_no_limits'),
              showSuccessBullet: true,
            },
          ]}
          isSelected={selectedPages.length > MAX_PAGES_ALLOWED_FOR_CHEAP_PRODUCT ? selectedProductId === EXPENSIVE_PRODUCT_ID : selectedProductId === CHEAP_PRODUCT_ID}
        />
      </div>
    </div>
  );
}
